// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// This content was prepared with the assistance of AI tools, reviewed by a
// human, and published by Techsafe Teknoloji ve Muhendislik A.S. The publishing
// entity is responsible for accuracy and compliance.
// Bu icerik, yapay zeka destekli araclar kullanilarak hazirlanmis, insan
// incelemesinden gecirilmis ve Techsafe Teknoloji ve Muhendislik A.S.
// tarafindan yayimlanmistir. Icerigin dogrulugundan yayimlayan kurulus
// sorumludur.
//
// ─────────────────────────────────────────────────────────────────────────
// NC COMPONENT CANDIDATE — "NCGuidedTour", per
// nc-components/usage-notes/guided-tour.md and layouts/first-use-tour.md.
// Built locally per this AU; do not import across AUs until it goes through
// review + playground + the normal issue → branch → build → MR promotion
// flow (governance/nc-component-promotion.md).
//
// Footer shell is identical regardless of step count: a low-emphasis
// "Skip tour", a "Back" control (hidden on the first step), and a primary
// control that reads "Done" on the last step.
// ─────────────────────────────────────────────────────────────────────────

import { useEffect, useLayoutEffect, useRef, useState } from "react";

import { Button } from "@/primitives/button";

export interface NCGuidedTourStep {
  /** CSS selector for the element this step highlights. */
  targetSelector: string;
  title: string;
  body: string;
}

export interface NCGuidedTourProps {
  steps: readonly NCGuidedTourStep[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function NCGuidedTour({ steps, open, onOpenChange }: NCGuidedTourProps) {
  const [stepIndex, setStepIndex] = useState(0);
  const cardRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  const step = steps[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === steps.length - 1;

  useEffect(() => {
    if (open) setStepIndex(0);
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !step) return;
    const target = document.querySelector(step.targetSelector);
    if (!target) return;
    target.classList.add("nc-tour-highlight");

    function reposition() {
      const rect = target!.getBoundingClientRect();
      const cardRect = cardRef.current?.getBoundingClientRect();
      const width = cardRect?.width ?? 320;
      const height = cardRect?.height ?? 160;
      const margin = 16;
      const gap = 12;

      let top = rect.top - height - gap;
      top = Math.max(margin, Math.min(top, window.innerHeight - height - margin));

      let left = rect.left + rect.width / 2 - width / 2;
      left = Math.max(margin, Math.min(left, window.innerWidth - width - margin));

      setPosition({ top, left });
    }

    reposition();
    window.addEventListener("resize", reposition);
    cardRef.current?.focus();

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") onOpenChange(false);
    }
    document.addEventListener("keydown", onKeyDown);

    return () => {
      target!.classList.remove("nc-tour-highlight");
      window.removeEventListener("resize", reposition);
      document.removeEventListener("keydown", onKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, stepIndex]);

  if (!open || !step) return null;

  return (
    <>
      {/* Scrim — dismiss only through an explicit action (Skip, Done, Esc);
          never on an outside click, since a spotlighted screen invites
          exploration clicks that should not be mistaken for dismissal. */}
      <div className="fixed inset-0 z-20 bg-black/45" />

      <div
        ref={cardRef}
        role="dialog"
        aria-modal="true"
        aria-label="Guided tour"
        tabIndex={-1}
        className="fixed z-[22] flex w-80 max-w-[calc(100vw-2rem)] flex-col gap-nc-2 rounded-lg border border-border bg-card p-nc-5 focus:outline-none"
        style={position ? { top: position.top, left: position.left } : { top: -9999, left: -9999 }}
      >
        <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
          Guided tour
        </p>
        <p className="text-base font-medium text-foreground">{step.title}</p>
        <p className="text-sm text-muted-foreground">{step.body}</p>

        <div className="mt-nc-1 flex items-center justify-between gap-nc-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => onOpenChange(false)}>
            Skip tour
          </Button>

          <span className="text-xs text-muted-foreground">
            {stepIndex + 1} / {steps.length}
          </span>

          <div className="flex items-center gap-nc-2">
            {!isFirst ? (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={() => setStepIndex((i) => Math.max(0, i - 1))}
              >
                Back
              </Button>
            ) : null}
            <Button
              type="button"
              size="sm"
              onClick={() => {
                if (isLast) onOpenChange(false);
                else setStepIndex((i) => Math.min(steps.length - 1, i + 1));
              }}
            >
              {isLast ? "Done" : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
